import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BlockEditor, Uploader, Field, inp } from "./BlockEditor";
import { mediaUrl } from "../../../lib/blogApi";
import { NewsletterPanel } from "./NewsletterPanel";

const EMPTY = { title: "", slug: "", excerpt: "", lang: "no", tags: [], cover_media_id: null, cover_alt: "", blocks: [{ type: "text" }], status: "draft", hidden: false };
const btn = "font-display tracking-[0.18em] text-[11px] uppercase px-3 py-2 border transition-colors disabled:opacity-50";
const err = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Noe gikk galt."; };
const fmtWhen = (iso) => new Date(iso).toLocaleString("nb-NO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const toLocalInput = (iso) => { if (!iso) return ""; const t = new Date(iso); return new Date(t - t.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
const STATUS_LABEL = { draft: "utkast", published: "publisert", scheduled: "planlagt" };

function ScheduleBox({ d, busy, onSchedule }) {
  const [when, setWhen] = useState(toLocalInput(d.scheduled_at));
  const future = when && new Date(when) > new Date();
  return (
    <div data-testid="blog-schedule-box" className="flex items-center gap-2 flex-wrap">
      <input type="datetime-local" data-testid="blog-schedule-at" className={`${inp} !w-auto`} value={when} min={toLocalInput(new Date().toISOString())}
        onChange={(e) => setWhen(e.target.value)} aria-label="Publiseringstidspunkt" />
      <button data-testid="blog-schedule" disabled={busy || !d.title || !future} onClick={() => onSchedule(new Date(when).toISOString())}
        className={`${btn} border-l3 text-l3 hover:bg-l3 hover:text-space`}>Planlegg</button>
    </div>
  );
}

function SourcesPanel({ ax, onChanged }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState({ url: "", title: "", text: "" });
  const load = () => ax.get("/admin/blog/sources").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async () => {
    setBusy(true);
    try { const { data } = await ax.post("/admin/blog/ingest"); toast.success(`Hentet: ${data.reduce((a, r) => a + (r.added || 0), 0)} nye innlegg`); load(); onChanged(); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const addManual = async () => {
    setBusy(true);
    try { await ax.post("/admin/blog/ingest/manual", { url: manual.url, title: manual.title || null, text: manual.text || null }); toast.success("Lagt til."); setManual({ url: "", title: "", text: "" }); load(); onChanged(); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const color = { ok: "#10b981", push: "#10b981", blocked: "#ff7f50", unreachable: "#ff7f50", empty: "#e6c84a", never: "#a3acb9" };
  const downloadPlugin = async () => {
    try {
      const { data } = await ax.get("/admin/blog/wp-plugin.zip", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data); a.download = "nickolass-twin-sync.zip"; a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error("Kunne ikke laste ned utvidelsen."); }
  };
  return (
    <div data-testid="blog-sources" className="intel-panel p-5 mt-6" style={{ "--panel-accent": "#e6c84a", borderLeftColor: "#e6c84a" }}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="font-display tracking-[0.2em] text-[11px] uppercase text-amber">// INNHENTING FRA EGNE NETTSTEDER · daglig kl. 05:00</div>
        <button data-testid="blog-ingest-now" onClick={run} disabled={busy} className={`${btn} border-amber text-amber hover:bg-amber hover:text-space`}>{busy ? "Henter…" : "Hent nå"}</button>
      </div>
      <ul className="mt-4 grid md:grid-cols-5 gap-2 font-term text-[12px]">
        {rows.map((r) => (
          <li key={r.domain} data-testid={`blog-source-${r.domain}`} className="border border-edge p-2">
            <div className="text-coolwhite">{r.domain}</div>
            <div style={{ color: color[r.status] || "#a3acb9" }} className="uppercase tracking-[0.12em] text-[11px] mt-1">{r.status === "push" ? "push (WP)" : r.status}</div>
            <div className="text-dim text-[11px]">{r.total ?? 0} innlegg{(r.last_push || r.last_run) ? ` · ${new Date(r.last_push || r.last_run).toLocaleString()}` : ""}</div>
          </li>
        ))}
      </ul>
      <div data-testid="blog-wp-plugin" className="mt-4 border border-l3/50 p-3 flex items-start justify-between gap-4 flex-wrap">
        <div className="font-term text-[12px] text-coolwhite/90 max-w-[760px]">
          <div className="text-l3 font-display tracking-[0.18em] text-[11px] uppercase">WordPress-utvidelse · for blokkerte nettsteder</div>
          <p className="mt-1">Installer på nettpilot.com, nettpilot.no og sprintcamp.no: Utvidelser → Legg til ny → Last opp utvidelse → velg zip-fila → Aktiver. Nye innlegg sendes hit når du publiserer. Eldre innlegg: Verktøy → Twin Sync → «Send til bloggen». Fila inneholder en hemmelig nøkkel — ikke del den.</p>
        </div>
        <button data-testid="blog-wp-plugin-download" onClick={downloadPlugin} className={`${btn} border-l3 text-l3 hover:bg-l3 hover:text-space`}>Last ned utvidelse (.zip)</button>
      </div>
      <p className="mt-3 font-term text-[11.5px] text-dim">«blocked» = nettstedet (SiteGround/Cloudflare) slipper ikke inn boten. Lim inn lenke (og evt. tekst) under, eller hvitelist boten hos hosten.</p>
      <div className="mt-3 grid md:grid-cols-3 gap-2">
        <input data-testid="blog-manual-url" className={inp} placeholder="https://nettpilot.no/artikkel/…" value={manual.url} onChange={(e) => setManual({ ...manual, url: e.target.value })} />
        <input data-testid="blog-manual-title" className={inp} placeholder="Tittel (valgfri)" value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })} />
        <button data-testid="blog-manual-submit" onClick={addManual} disabled={busy || !manual.url} className={`${btn} border-edge text-coolwhite hover:border-l3`}>+ Legg til fra lenke</button>
        <textarea data-testid="blog-manual-text" className={`${inp} md:col-span-3`} rows={2} placeholder="Artikkeltekst (valgfri — trengs hvis siden blokkerer boten)" value={manual.text} onChange={(e) => setManual({ ...manual, text: e.target.value })} />
      </div>
    </div>
  );
}

function PostEditor({ ax, initial, onClose }) {
  const [d, setD] = useState({ ...EMPTY, ...initial, tagsText: (initial?.tags || []).join(", ") });
  const [busy, setBusy] = useState(false);
  const [altBusy, setAltBusy] = useState(false);
  const [sugBusy, setSugBusy] = useState(false);
  const suggestAI = async () => {
    setSugBusy(true);
    const text = (d.blocks || []).map((b) => b.text || b.transcript || b.caption || "").filter(Boolean).join("\n\n");
    try {
      const { data } = await ax.post("/admin/blog/suggest", { title: d.title, excerpt: d.excerpt, lang: d.lang, text });
      setD((cur) => ({ ...cur, title: data.title || cur.title, excerpt: data.excerpt || cur.excerpt, tagsText: data.tags?.length ? data.tags.join(", ") : cur.tagsText }));
      toast.success("Forslag fylt inn — rett gjerne før du lagrer.");
    } catch (e) { toast.error(err(e)); } finally { setSugBusy(false); }
  };
  const save = async (status, scheduledAt = null) => {
    setBusy(true);
    const body = { ...d, status, scheduled_at: scheduledAt, tags: d.tagsText.split(",").map((t) => t.trim()).filter(Boolean), slug: d.slug || null };
    delete body.tagsText;
    ["id", "source", "source_name", "source_url", "created_at", "updated_at", "published_at", "reading_min", "deleted", "cover_url"].forEach((k) => delete body[k]);
    try {
      const { data } = d.id ? await ax.put(`/admin/blog/posts/${d.id}`, body) : await ax.post("/admin/blog/posts", body);
      toast.success(data.status === "scheduled" ? `Planlagt til ${fmtWhen(data.scheduled_at)}.` : data.status === "published" ? "Publisert." : "Utkast lagret.");
      setD({ ...d, ...data, tagsText: data.tags.join(", ") });
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const altAI = async () => {
    setAltBusy(true);
    try { const { data } = await ax.post("/admin/blog/alt-text", { media_id: d.cover_media_id, lang: d.lang }); setD({ ...d, cover_alt: data.alt }); }
    catch (e) { toast.error(err(e)); } finally { setAltBusy(false); }
  };
  return (
    <div data-testid="blog-editor" className="mt-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onClose} data-testid="blog-editor-back" className={`${btn} border-edge text-coolwhite`}>← Tilbake</button>
        <div className="flex gap-2 flex-wrap">
          {d.slug && d.status === "published" && <a href={`/blog/${d.slug}`} target="_blank" rel="noreferrer" className={`${btn} border-edge text-coolwhite`}>Vis ↗</a>}
          <button data-testid="blog-save-draft" disabled={busy || !d.title} onClick={() => save("draft")} className={`${btn} border-edge text-coolwhite hover:border-amber`}>Lagre utkast</button>
          <button data-testid="blog-publish" disabled={busy || !d.title} onClick={() => save("published")} className={`${btn} bg-nettred border-nettred text-space hover:bg-amber hover:text-space`}>{d.status === "scheduled" ? "Publiser nå" : "Publiser"}</button>
        </div>
      </div>
      <div className="flex items-center justify-end gap-3 flex-wrap">
        <span className="font-term text-[11px] text-dim">eller planlegg (din lokale tid):</span>
        <ScheduleBox key={d.scheduled_at || "new"} d={d} busy={busy} onSchedule={(iso) => save("scheduled", iso)} />
      </div>
      <div data-testid="blog-editor-status" className="font-term text-[11px] text-dim">// status: {STATUS_LABEL[d.status] || d.status}{d.status === "scheduled" && d.scheduled_at ? ` · publiseres ${fmtWhen(d.scheduled_at)}` : ""}{d.source && d.source !== "own" ? ` · hentet fra ${d.source}` : ""}</div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="font-term text-[11.5px] text-dim">Skriv teksten først — AI foreslår tittel, ingress og tagger i din stemme.</span>
        <button type="button" data-testid="blog-suggest-ai" disabled={sugBusy || busy} onClick={suggestAI}
          className={`${btn} border-l3 text-l3 hover:bg-l3 hover:text-space`}>{sugBusy ? "Tenker…" : "AI-forslag: tittel, ingress, tagger"}</button>
      </div>
      <Field label="Tittel"><input data-testid="blog-title" className={inp} value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} /></Field>
      <div className="grid md:grid-cols-3 gap-3">
        <Field label="Slug (valgfri)"><input data-testid="blog-slug" className={inp} value={d.slug || ""} onChange={(e) => setD({ ...d, slug: e.target.value })} /></Field>
        <Field label="Språk">
          <select data-testid="blog-lang" className={inp} value={d.lang} onChange={(e) => setD({ ...d, lang: e.target.value })}>
            <option value="no">Norsk</option><option value="en">English</option><option value="da">Dansk</option>
          </select>
        </Field>
        <Field label="Tagger (komma)"><input data-testid="blog-tags" className={inp} value={d.tagsText} onChange={(e) => setD({ ...d, tagsText: e.target.value })} /></Field>
      </div>
      <Field label="Ingress"><textarea data-testid="blog-excerpt" rows={2} className={inp} value={d.excerpt} onChange={(e) => setD({ ...d, excerpt: e.target.value })} /></Field>
      <div className="intel-panel p-4 space-y-3" style={{ "--panel-accent": "#10b981", borderLeftColor: "#10b981" }}>
        <div className="font-display tracking-[0.2em] text-[11px] uppercase text-l3">// FORSIDEBILDE</div>
        {d.cover_media_id && <img src={mediaUrl(d.cover_media_id)} alt="" className="max-h-40 border border-edge" />}
        {!d.cover_media_id && d.cover_url && <img src={d.cover_url} alt="" className="max-h-40 border border-edge" />}
        <Uploader ax={ax} accept="image/*" testid="blog-cover-upload" label={d.cover_media_id ? "Bytt bilde" : "Last opp forsidebilde"} onDone={(m) => setD({ ...d, cover_media_id: m.id })} />
        <div className="flex gap-2">
          <input data-testid="blog-cover-alt" className={inp} placeholder="Alt-tekst (påkrevd når bilde er valgt)" value={d.cover_alt || ""} onChange={(e) => setD({ ...d, cover_alt: e.target.value })} />
          <button type="button" data-testid="blog-cover-alt-ai" disabled={!d.cover_media_id || altBusy} onClick={altAI} className="shrink-0 font-display tracking-[0.15em] text-[10px] uppercase px-3 border border-l3 text-l3 disabled:opacity-40">{altBusy ? "…" : "AI-forslag"}</button>
        </div>
      </div>
      <BlockEditor blocks={d.blocks} onChange={(blocks) => setD({ ...d, blocks })} ax={ax} lang={d.lang} />
    </div>
  );
}

function IntroEditor({ ax, post, onSaved, onClose }) {
  const [text, setText] = useState(post.excerpt || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (post.excerpt === undefined) ax.get(`/admin/blog/posts/${post.id}`).then(({ data }) => setText(data.excerpt || ""));
  }, [post.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true);
    try { await ax.put(`/admin/blog/posts/${post.id}/excerpt`, { excerpt: text }); toast.success("Ingress lagret."); onSaved(); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="blog-intro-editor" className="w-full mt-2 space-y-2">
      <textarea data-testid="blog-intro-text" rows={4} maxLength={900} className={inp} value={text} onChange={(e) => setText(e.target.value)} />
      <div className="flex items-center gap-2 flex-wrap">
        <button data-testid="blog-intro-save" disabled={busy || !text.trim()} onClick={save} className={`${btn} bg-nettred border-nettred text-space hover:bg-amber hover:text-space`}>{busy ? "…" : "Lagre ingress"}</button>
        <button data-testid="blog-intro-cancel" onClick={onClose} className={`${btn} border-edge text-coolwhite/80`}>avbryt</button>
        <span className="font-term text-[11px] text-dim">{text.length}/900 · lenken til originalartikkelen beholdes</span>
      </div>
    </div>
  );
}

function PostStats({ s }) {
  const x = s || {};
  const cell = (label, d30, total, key) => (
    <span data-testid={`blog-stat-${key}`} className="whitespace-nowrap">
      <span className="text-dim">{label}</span> <span className="text-coolwhite">{d30 ?? 0}</span>
      <span className="text-dim"> / {total ?? 0}</span>
    </span>
  );
  return (
    <div data-testid="blog-row-stats" className="flex flex-wrap gap-x-4 gap-y-1 font-term text-[11.5px]" title="siste 30 dager / totalt">
      {cell("lesere", x.readers_30, x.readers_total, "readers")}
      {cell("visninger", x.views_30, x.views_total, "views")}
      {cell("sitert i chat", x.cites_30, x.cites_total, "cites")}
    </div>
  );
}

export const BlogTab = ({ ax }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [editing, setEditing] = useState(null);
  const [introFor, setIntroFor] = useState(null);
  const load = () => {
    ax.get("/admin/blog/posts").then(({ data }) => setItems(data)).catch((e) => toast.error(err(e)));
    ax.get("/admin/blog/stats").then(({ data }) => setStats(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const open = async (id) => { const { data } = await ax.get(`/admin/blog/posts/${id}`); setEditing(data); };
  const toggleHidden = async (p) => {
    const { data } = await ax.get(`/admin/blog/posts/${p.id}`);
    try { await ax.put(`/admin/blog/posts/${p.id}`, { ...data, hidden: !p.hidden }); load(); } catch (e) { toast.error(err(e)); }
  };
  const remove = async (p) => { if (!window.confirm(`Slette «${p.title}»?`)) return; await ax.delete(`/admin/blog/posts/${p.id}`); load(); };
  if (editing) return <PostEditor ax={ax} initial={editing} onClose={() => { setEditing(null); load(); }} />;
  return (
    <div data-testid="blog-tab">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="font-display font-bold text-2xl text-coolwhite uppercase">Blogg</h2>
        <button data-testid="blog-new-post" onClick={() => setEditing({})} className={`${btn} bg-nettred border-nettred text-space hover:bg-amber hover:text-space`}>+ Nytt innlegg</button>
      </div>
      <p className="mt-1 font-term text-[13px] text-dim">Publisert vises på /blog og /api/blog (maskinlesbar), og i sitemap, RSS og llms.txt.</p>
      <p className="mt-1 font-term text-[12px] text-dim">Tall per innlegg: siste 30 dager / totalt. Lesere = unike per dag (uten cookies), visninger = alle sidevisninger, sitert = ganger tvillingen viste til innlegget i chatten.</p>
      <SourcesPanel ax={ax} onChanged={load} />
      <NewsletterPanel ax={ax} />
      <ul className="mt-6 space-y-2">
        {items.length === 0 && <li className="font-term text-dim">// ingen innlegg ennå</li>}
        {items.map((p) => (
          <li key={p.id} data-testid="blog-admin-row" className="intel-panel p-3 flex items-center justify-between gap-3 flex-wrap"
            style={{ "--panel-accent": p.status === "published" && !p.hidden ? "#10b981" : "#a3acb9", borderLeftColor: p.status === "published" && !p.hidden ? "#10b981" : "#a3acb9" }}>
            <div className="min-w-0">
              <div className="text-coolwhite font-term text-[13.5px] truncate">{p.title}</div>
              <div className="text-dim font-term text-[11px]"><span data-testid="blog-row-status" className={p.status === "scheduled" ? "text-l3" : ""}>{STATUS_LABEL[p.status] || p.status}{p.status === "scheduled" && p.scheduled_at ? ` ${fmtWhen(p.scheduled_at)}` : ""}</span>{p.hidden ? " · skjult" : ""} · {p.lang} · {p.source === "own" ? "egen" : p.source}{p.status === "scheduled" ? "" : ` · ${(p.published_at || p.updated_at || "").slice(0, 10)}`}</div>
              {p.status === "published" && <div className="mt-1"><PostStats s={stats[p.slug]} /></div>}
            </div>
            <div className="flex gap-1">
              {p.source !== "own" && <button onClick={() => setIntroFor(introFor === p.id ? null : p.id)} data-testid="blog-row-intro" className={`${btn} border-l3 text-l3`}>ingress</button>}
              <button onClick={() => open(p.id)} data-testid="blog-row-edit" className={`${btn} border-amber text-amber`}>rediger</button>
              <button onClick={() => toggleHidden(p)} data-testid="blog-row-hide" className={`${btn} border-edge text-coolwhite/80`}>{p.hidden ? "vis" : "skjul"}</button>
              <button onClick={() => remove(p)} data-testid="blog-row-delete" className={`${btn} border-edge text-coolwhite/80 hover:text-nettred`}>slett</button>
            </div>
            {introFor === p.id && <IntroEditor ax={ax} post={p} onClose={() => setIntroFor(null)} onSaved={() => { setIntroFor(null); load(); }} />}
          </li>
        ))}
      </ul>
    </div>
  );
};
